import { useEffect, useState } from "react"
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  PublicClientApplication,
} from "@azure/msal-browser"
import { useMsal } from "@azure/msal-react"

const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? ""
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? ""
const scope = import.meta.env.VITE_ENTRA_SCOPE ?? ""
const devAuth = import.meta.env.VITE_DEV_AUTH === "true"

const REQUESTED_LOCATION_KEY = "auth.requestedLocation"

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId,
    authority: tenantId ? `https://login.microsoftonline.com/${tenantId}` : undefined,
    redirectUri: "/",
  },
  cache: { cacheLocation: "sessionStorage" },
})

export const loginRequest = { scopes: scope ? [scope] : [] }

const DEV_TOKEN = "dev-bypass-no-real-token"

function stashRequestedLocation(): void {
  const path = window.location.pathname + window.location.search
  if (path !== "/") {
    sessionStorage.setItem(REQUESTED_LOCATION_KEY, path)
  }
}

export function clearRequestedLocation(): void {
  sessionStorage.removeItem(REQUESTED_LOCATION_KEY)
}

export function getRequestedLocation(): string | null {
  return sessionStorage.getItem(REQUESTED_LOCATION_KEY)
}

export type Session = {
  token: string | null
  /** The sign-in state is settled: either a token is held, or nobody is signed in. */
  isReady: boolean
  /** Start the interactive sign-in. Only ever called from a click. */
  signIn: () => void
}

export function useAccessToken(): Session {
  const { instance, accounts, inProgress } = useMsal()
  const [token, setToken] = useState<string | null>(devAuth ? DEV_TOKEN : null)
  const [isReady, setIsReady] = useState(devAuth)

  const signIn = () => {
    stashRequestedLocation()
    void instance.loginRedirect(loginRequest)
  }

  useEffect(() => {
    if (devAuth) return
    // msal-react processes the redirect response (handleRedirectPromise)
    // asynchronously on mount; inProgress stays non-"none" until that
    // settles. Firing loginRedirect/acquireTokenRedirect before then races
    // msal-react's own handling -- each render sees accounts still empty,
    // fires another interactive redirect, which (with an active Microsoft
    // SSO session) completes near-instantly and repeats, producing a rapid
    // sign-in loop that Microsoft's login page eventually breaks with a
    // generic "couldn't sign you in" error. Only act once msal-react is idle.
    if (inProgress !== InteractionStatus.None) return
    const account = accounts[0]
    // Nobody is signed in: nothing to acquire. `signedOut` below settles the
    // state without a setState here, and the sign-in screen offers the button
    // rather than redirecting on our own -- an automatic redirect is what
    // produced the loop described above, and a click cannot loop.
    if (!account) return
    instance
      .acquireTokenSilent({ ...loginRequest, account })
      .then((result) => {
        setToken(result.accessToken)
        setIsReady(true)
      })
      .catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          // The session needs a human again (expired, revoked, MFA). Same rule:
          // show the button, never redirect unprompted.
          setToken(null)
          setIsReady(true)
          return
        }
        // Anything else -- a network blip, Entra unreachable, a client error --
        // was rethrown into a promise nobody awaited. `isReady` stayed false, so
        // the console sat on a loading skeleton forever: no error, no way out, and
        // a refresh took the same silent path to the same place. Settle and show
        // the sign-in screen instead, because a button is a recovery and an
        // eternal skeleton is not.
        console.error("could not acquire a token silently", error)
        setToken(null)
        setIsReady(true)
      })
  }, [instance, accounts, inProgress])

  // Settled without a token: MSAL is idle and holds no account. Derived rather
  // than stored, so the effect never has to setState synchronously.
  const signedOut = !devAuth && inProgress === InteractionStatus.None && accounts.length === 0

  return { token, isReady: isReady || signedOut, signIn }
}
